import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, useQuery, useMutation, Id } from '../lib/convexApi';

const TournamentDashboard: React.FC = () => {
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const tournament = useQuery(api.tournaments.getTournamentDetails, 
    tournamentId ? { tournamentId: tournamentId as Id<"tournaments"> } : "skip"
  );
  const lockTournament = useMutation(api.tournaments.lockTournament);
  const removeFromTournament = useMutation(api.players.removeFromTournament);
  
  const [isLocking, setIsLocking] = useState(false);
  const [removingPlayer, setRemovingPlayer] = useState<string | null>(null);

  const handleLockTournament = async () => {
    if (!tournamentId || !tournament) return;
    
    const confirmed = window.confirm(
      'Are you sure you want to lock this tournament? No new players will be able to register.'
    );
    
    if (!confirmed) return;

    setIsLocking(true);
    try {
      await lockTournament({ tournamentId: tournamentId as Id<"tournaments"> });
    } catch (err) {
      alert('Failed to lock tournament');
    } finally {
      setIsLocking(false);
    }
  };

  const handleRemovePlayer = async (playerId: Id<"players">, playerName: string) => {
    if (!tournamentId) return;
    
    const confirmed = window.confirm(`Remove ${playerName} from the tournament?`);
    if (!confirmed) return;

    setRemovingPlayer(playerId);
    try {
      await removeFromTournament({
        tournamentId: tournamentId as Id<"tournaments">,
        playerId,
      });
    } catch (err) {
      alert('Failed to remove player');
    } finally {
      setRemovingPlayer(null);
    }
  };

  const shareOnWhatsApp = () => {
    if (!tournament) return;
    
    const shareUrl = `${window.location.origin}/join/${tournament.inviteCode}`;
    const shareText = encodeURIComponent(
      `🎾 Join our padel tournament!\n\n${tournament.name}\n📅 ${new Date(tournament.date).toLocaleDateString()}\n📍 ${tournament.club}\n\nClick here to register: ${shareUrl}`
    );
    
    window.open(`https://wa.me/?text=${shareText}`, '_blank');
  };

  const copyInviteLink = () => {
    if (!tournament) return;
    
    const shareUrl = `${window.location.origin}/join/${tournament.inviteCode}`;
    navigator.clipboard.writeText(shareUrl).then(() => {
      alert('Invite link copied to clipboard!');
    });
  };

  if (!tournamentId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Invalid Tournament</h1>
          <Link to="/" className="text-blue-600 hover:text-blue-800">← Back to Home</Link>
        </div>
      </div>
    );
  }

  if (tournament === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Loading tournament...</p>
        </div>
      </div>
    );
  }

  if (tournament === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Tournament Not Found</h1>
          <Link to="/" className="text-blue-600 hover:text-blue-800">← Back to Home</Link>
        </div>
      </div>
    );
  }

  const confirmedPlayers = tournament?.registrations?.filter((r: any) => r.status === 'confirmed') || [];
  const waitlistPlayers = tournament?.registrations?.filter((r: any) => r.status === 'waitlist') || [];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Tournament Dashboard</h1>
          <Link to="/" className="text-blue-600 hover:text-blue-800">← Back to Home</Link>
        </div>

        {/* Tournament Info */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{tournament.name}</h2>
              <div className="space-y-1 text-gray-600">
                <p>📅 {new Date(tournament.date).toLocaleDateString()} at {new Date(tournament.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                <p>📍 {tournament.club}</p>
                <p>🎾 Format: {tournament.format.charAt(0).toUpperCase() + tournament.format.slice(1)}</p>
                <p>👥 {confirmedPlayers.length}/{tournament.maxPlayers} confirmed players</p>
              </div>
            </div>
            <div className="flex flex-col space-y-2">
              {tournament.status === 'open' && (
                <button
                  onClick={handleLockTournament}
                  disabled={isLocking}
                  className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg transition duration-200"
                >
                  {isLocking ? 'Locking...' : '🔒 Lock Tournament'}
                </button>
              )}
              {tournament.status === 'locked' && (
                <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium">
                  🔒 Locked
                </span>
              )}
            </div>
          </div>

          {/* Invite Section */}
          <div className="border-t pt-4">
            <h3 className="font-semibold text-gray-900 mb-3">Share Tournament</h3>
            <div className="flex items-center space-x-2 mb-3">
              <code className="bg-gray-100 px-3 py-2 rounded text-lg font-bold text-blue-600 flex-1">
                {tournament.inviteCode}
              </code>
              <button
                onClick={copyInviteLink}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition duration-200"
              >
                📋 Copy Link
              </button>
              <button
                onClick={shareOnWhatsApp}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition duration-200"
              >
                📱 WhatsApp
              </button>
            </div>
            <p className="text-sm text-gray-500">
              Share this invite code or link with players to let them join your tournament.
            </p>
          </div>
        </div>

        {/* Players List */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Confirmed Players */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Confirmed Players ({confirmedPlayers.length})
            </h3>
            
            {confirmedPlayers.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No confirmed players yet</p>
            ) : (
              <div className="space-y-3">
                {confirmedPlayers.map((registration: any) => (
                  <div key={registration._id} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-semibold text-gray-900">
                          {registration.player?.name}
                        </h4>
                        <p className="text-sm text-gray-600">
                          {registration.player?.phone}
                        </p>
                        {registration.partner && (
                          <p className="text-sm text-blue-600 mt-1">
                            Partner: {registration.partner.name}
                            {registration.partner.phone && ` (${registration.partner.phone})`}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => handleRemovePlayer(registration.playerId, registration.player?.name || 'Player')}
                        disabled={removingPlayer === registration.playerId}
                        className="text-red-600 hover:text-red-800 disabled:opacity-50 text-sm"
                      >
                        {removingPlayer === registration.playerId ? '...' : '❌'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Waitlist */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Waitlist ({waitlistPlayers.length})
            </h3>
            
            {waitlistPlayers.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No players on waitlist</p>
            ) : (
              <div className="space-y-3">
                {waitlistPlayers.map((registration: any) => (
                  <div key={registration._id} className="border border-yellow-200 rounded-lg p-3 bg-yellow-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-semibold text-gray-900">
                          {registration.player?.name}
                        </h4>
                        <p className="text-sm text-gray-600">
                          {registration.player?.phone}
                        </p>
                        {registration.partner && (
                          <p className="text-sm text-blue-600 mt-1">
                            Partner: {registration.partner.name}
                            {registration.partner.phone && ` (${registration.partner.phone})`}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => handleRemovePlayer(registration.playerId, registration.player?.name || 'Player')}
                        disabled={removingPlayer === registration.playerId}
                        className="text-red-600 hover:text-red-800 disabled:opacity-50 text-sm"
                      >
                        {removingPlayer === registration.playerId ? '...' : '❌'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TournamentDashboard;